'use client';

import { useSlotId } from '@react-aria/utils';
import clsx from 'clsx';
import type { ForwardedRef } from 'react';
import {
  Provider as RACProvider,
  Switch as RACSwitch,
  type SwitchProps as RACSwitchProps,
  TextContext as RACTextContext,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { IconCheckBold, IconCloseBold } from '../../icons/index.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { FormFieldDescription } from '../internal/form-field/form-field.js';
import type { AriaLabelingProps, CommonProps, StringLikeChildren } from '../types.js';
import { Text } from '../typography/text.js';

export interface SwitchProps
  extends
    CommonProps,
    AriaLabelingProps,
    Pick<
      RACSwitchProps,
      | 'onChange'
      | 'isSelected'
      | 'isDisabled'
      | 'isReadOnly'
      | 'defaultSelected'
      | 'autoFocus'
      | 'form'
      | 'name'
      | 'value'
    > {
  /** The label rendered next to the switch. */
  children?: StringLikeChildren;
  /** A description for the switch. Provides a hint such as specific requirements for which setting to choose. */
  description?: string;
  /**
   * Controls where the label is rendered relative to the switch.
   * @default 'end'
   */
  labelPosition?: 'top' | 'start' | 'end';
  /**
   * Whether the switch should take up the whole available width. Enabling this option will add space between the switch and its label.
   * @default false
   */
  fullWidth?: boolean;
}

function Switch(
  {
    children,
    description,
    labelPosition = 'end',
    fullWidth = false,
    'aria-describedby': ariaDescribedBy,
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: SwitchProps,
  ref: ForwardedRef<HTMLLabelElement>,
) {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!children && !ariaLabel && !ariaLabelledBy) {
      console.warn('Switch requires one of children / aria-label / aria-labelledby for accessibility');
    }
  }, [children, ariaLabel, ariaLabelledBy]);

  const descriptionId = useSlotId([Boolean(description)]);

  const label = (
    <>
      {children && (
        <Text as="span" variant="medium">
          {children}
        </Text>
      )}
    </>
  );

  return (
    <RACProvider values={[[RACTextContext, { slots: { description: { id: descriptionId } } }]]}>
      <div
        className={clsx('cim-switch', UNSAFE_className)}
        style={UNSAFE_style}
        data-label-position={labelPosition}
        data-full-width={fullWidth || undefined}
      >
        <RACSwitch
          {...props}
          className="cim-switch-control-wrapper"
          ref={ref}
          aria-describedby={descriptionId || ariaDescribedBy ? [descriptionId, ariaDescribedBy].join(' ') : undefined}
        >
          {labelPosition !== 'end' && label}

          <div className="cim-switch-track-wrapper" aria-hidden>
            <div className="cim-switch-track">
              <IconCheckBold UNSAFE_className="cim-switch-icon-on" />
              <IconCloseBold UNSAFE_className="cim-switch-icon-off" />
              <div className="cim-switch-track-head" />
            </div>
          </div>

          {labelPosition === 'end' && label}
        </RACSwitch>

        <FormFieldDescription>{description}</FormFieldDescription>
      </div>
    </RACProvider>
  );
}

/**
 * Allows users to turn a setting on or off.
 *
 * See [switch usage guidelines](https://ui.cimpress.io/components/switch/).
 */
const _Switch = withStyleProps(forwardRef(Switch), 'Switch');

export { _Switch as Switch };
